import type { SuperCol } from "./types/dataTable.ts";
import * as constantsBasketball from "./constants.basketball.ts";
import type { CompositeWeights, Phase, DraftType, MoodTrait } from "./types.ts";

export const DIFFICULTY = {
	Easy: -0.25,
	Normal: 0,
	Hard: 0.25,
	Insane: 1,
};

export const DRAFT_BY_TEAM_OVR = false;

export const LEAGUE_DATABASE_VERSION = 74;

export const NO_LOTTERY_DRAFT_TYPES = new Set<DraftType>([
	"freeAgents",
	"noLottery",
	"noLotteryReverse",
	"random",
]);

export const PHASE = {
	EXPANSION_DRAFT: -2,
	FANTASY_DRAFT: -1,
	PRESEASON: 0,
	REGULAR_SEASON: 1,
	AFTER_TRADE_DEADLINE: 2,
	PLAYOFFS: 3,
	DRAFT_LOTTERY: 4,
	DRAFT: 5,
	AFTER_DRAFT: 6,
	RESIGN_PLAYERS: 7,
	FREE_AGENCY: 8,
} satisfies Record<string, Phase>;

export const REMAINING_PLAYOFF_TEAMS_PHASES = new Set<Phase>([
	PHASE.REGULAR_SEASON,
	PHASE.AFTER_TRADE_DEADLINE,
	PHASE.PLAYOFFS,
]);

export const PLAYER = {
	FREE_AGENT: -1,
	UNDRAFTED: -2,
	RETIRED: -3,

	// Store current draft class here during fantasy draft
	UNDRAFTED_FANTASY_TEMP: -6,

	// Used for realStats when a team has been contracted
	DOES_NOT_EXIST: -7,

	// Used for summed up season stats for multiple teams
	TOT: -8,

	// THESE ARE OBSOLETE!
	UNDRAFTED_2: -4, // Next year's draft class
	UNDRAFTED_3: -5, // Next next year's draft class
};

export const PHASE_TEXT = {
	"-2": "expansion draft",
	"-1": "fantasy draft",
	"0": "preseason",
	"1": "regular season",
	"2": "regular season",
	"3": "playoffs",
	// Would be better to read from g.get("draftType")
	"4": "draft lottery",
	"5": "draft",
	"6": "after draft",
	"7": "re-sign players",
	"8": "free agency",
};

export const COMPOSITE_WEIGHTS: CompositeWeights =
	constantsBasketball.COMPOSITE_WEIGHTS;

export const PLAYER_SUMMARY: {
	[key: string]: {
		name: string;
		onlyShowIf?: string[];
		stats: string[];
		superCols?: {
			colspan: number;
			desc: string;
			title: string;
		}[];
	};
} = constantsBasketball.PLAYER_SUMMARY;

export const PLAYER_STATS_TABLES: {
	[key: string]: {
		name: string;
		onlyShowIf?: string[];
		stats: string[];
		superCols?: SuperCol[];
	};
} = constantsBasketball.PLAYER_STATS_TABLES;

export const RATINGS: any[] = constantsBasketball.RATINGS;

export const POSITION_COUNTS: {
	[key: string]: number;
} = constantsBasketball.POSITION_COUNTS;

export const POSITIONS: any[] = constantsBasketball.POSITIONS;

export const TEAM_STATS_TABLES: Record<
	string,
	{
		name: string;
		stats: string[];
		superCols?: SuperCol[];
	}
> = constantsBasketball.TEAM_STATS_TABLES;

export const TIME_BETWEEN_GAMES: string = "day";

export const MOOD_TRAITS: Record<MoodTrait, string> = {
	F: "Fame",
	L: "Loyalty",
	$: "Money",
	W: "Winning",
};

export const SIMPLE_AWARDS: Readonly<string[]> =
	constantsBasketball.SIMPLE_AWARDS;

export const AWARD_NAMES: Record<string, string> =
	constantsBasketball.AWARD_NAMES;

export const DEFAULT_CONFS = constantsBasketball.DEFAULT_CONFS;

export const DEFAULT_DIVS = constantsBasketball.DEFAULT_DIVS;

export const DEFAULT_STADIUM_CAPACITY = 25000;

export const COURT = "court";

export const GAME_NAME = "Basketball GM";

export const REAL_PLAYERS_INFO = {
	FIRST_SEASON_WITH_ALEXNOOB_ROSTERS: 2020,
	MIN_SEASON: 1947,
	MAX_SEASON: 2026,
	MAX_PHASE: PHASE.PLAYOFFS as Phase,
};

export const TIEBREAKERS = {
	commonOpponentsRecord: "Common opponents record",
	confRecordIfSame: "Conference record (same conf)",
	divRecordIfSame: "Division record (same div)",
	divWinner: "Division winner",
	headToHeadRecord: "Head-to-head record",
	marginOfVictory: "Margin of victory",
	strengthOfVictory: "Strength of victory",
	strengthOfSchedule: "Strength of schedule",
	coinFlip: "Coin flip",
};

// This is only applied by default in hockey, but it's still used in all sports if "pts" are explicitly requested and there is no formula set
export const DEFAULT_POINTS_FORMULA = "2*W+OTL+T";

export const DEFAULT_JERSEY = "jersey3";

// Target: 90% in playThroughInjuriesFactor
export const DEFAULT_PLAY_THROUGH_INJURIES: [number, number] = [0, 4];

export const ALL_STAR_GAME_ONLY = false;

export const DEFAULT_PHASE_CHANGE_REDIRECTS = [1, 3, 4, 5, 7, 8] as Phase[];

export const MOBILE_AD_BOTTOM_MARGIN = 52;

export const DEFAULT_TEAM_COLORS: [string, string, string] = [
	"#000000",
	"#cccccc",
	"#ffffff",
];

// Should actually be 7, but since timeouts are only used at the end of the game currently, it's silly to have those extra 5 timeouts lying around all game
export const STARTING_NUM_TIMEOUTS = 2;

export const SKILLS: Record<string, string> = {
	"3": "Three point shooter",
	A: "Athlete",
	B: "Ball handler",
	Di: "Interior defender",
	Dp: "Perimeter defender",
	Po: "Post scorer",
	Ps: "Passer",
	R: "Rebounder",
	V: "Volume scorer",
};

export const COLA_ALPHA = 1000;
export const COLA_OPT_OUT_PENALTY = 2000;

export const ERROR_MESSAGE_UNDEFINED_SEASON =
	"Undefined season - an error may have occurred while creating this league";
